import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { AppDispatch } from '@/store/store';
import type { PMSVendorData, PmsSourceKey } from '@/types/pms.types';
import {
  pmsConnectionApi,
  type SyncLogView,
  useGetSyncLogQuery,
  useSaveConnectionAndSyncMutation,
  useSyncPropertiesMutation,
} from '../services/pmsConnectionApi';
import {
  MAX_PENDING_POLLS,
  NO_LOG_ID,
  selectPmsSync,
  syncAbandoned,
  syncCompletionCleared,
  syncObserved,
  syncOwnerDetached,
  syncPollObserved,
  syncRequested,
  type SyncOrigin,
} from '../services/pmsSyncSlice';
import type { PmsAccountRef } from '../services/pmsAccount';

dayjs.extend(relativeTime);

/** 10 s while a sync runs (spec §2 decision 3); the hero and banner are foregrounded progress. */
export const SYNC_POLL_INTERVAL_MS = 10_000;

const NEVER: SyncLogView = { kind: 'never' };
/** Placeholder id for the optimistic running view — never a real log id. */
const OPTIMISTIC_ID = 'optimistic';

/**
 * Whether the backend took the sync request. `rejected` is a fast failure
 * (400/500 before any log row) — nothing was persisted or started. A 504 is
 * `accepted`: property-service's paginated pull outlives the gateway, but the
 * request (and, for saveAndSync, the saved body) went through.
 */
export type SyncRequestOutcome = 'accepted' | 'rejected';

export interface SyncProgress {
  sync: SyncLogView;
  startSync: (pmsSource: PmsSourceKey) => Promise<SyncRequestOutcome>;
  saveAndSync: (body: PMSVendorData) => Promise<SyncRequestOutcome>;
  lastSyncedLabel: string | null;
  /**
   * The fact: a sync started in this browser session is the current `complete`
   * log. React to this for side effects any surface owes (PMSSync's list
   * reset), regardless of who started it or whether they are still mounted.
   */
  completedInSession: boolean;
  /**
   * The story: origin of that session-started sync, or null. Compare with your
   * own origin to render "Property sync complete." Null when the fact is false
   * AND when the owning surface unmounted mid-sync (nobody left to tell it).
   */
  completedOrigin: SyncOrigin | null;
  /**
   * Origin of the RUNNING sync when this session started it (pending request
   * → `pendingOrigin`; observed new log → `startedOrigin`), else null. Lets a
   * banner say "Connected!" only to the surface whose save started the sync
   * and use passive wording for one started elsewhere.
   */
  runningOrigin: SyncOrigin | null;
}

export interface SyncProgressOptions {
  /** Which surface this instance belongs to; scopes the completion story (pmsSyncSlice.SyncOrigin). */
  origin: SyncOrigin;
  /** DV-3345: whose sync log; null = no account (fires nothing, reads never). */
  account: PmsAccountRef | null;
  /** Firing-only instance: no subscription, no polling. */
  skip?: boolean;
}

/** The log id a sync request is measured against, plus the poll-count seed that goes with it. */
interface SyncBaseline {
  fromLogId: string;
  fulfilledAt: number | null;
}

const idOf = (view: SyncLogView): string => (view.kind === 'never' ? NO_LOG_ID : view.id);

/**
 * Manual stand-in for RTK Query's `skipPollingIfUnfocused` subscription
 * option: this repo pins @reduxjs/toolkit 1.9.7 (see
 * docs/engineering-backlog.md), which predates that option
 * (added in 2.2.4) — it isn't present in the installed package at all, so
 * passing it is a type error, not just an unused flag. Delete this and pass
 * `skipPollingIfUnfocused: true` to useGetSyncLogQuery once RTK Query is
 * upgraded past 2.2.4.
 */
const usePageVisible = (): boolean => {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );
  useEffect(() => {
    const onVisibilityChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);
  return visible;
};

const logSyncError = (error: unknown): number | undefined => {
  const status = (error as { status?: number }).status;
  if (status === 504) {
    console.error('Gateway Timeout (504): Sync request failed.');
  } else {
    console.error('Error syncing properties:', error);
  }
  return status;
};

/**
 * One view of property-sync state for every page (Properties list, vendor
 * settings, Integrations landing). Server truth is GET …/syncLogs, cached by
 * RTK and polled only while a sync is running. The only client-side state is
 * the shared `pendingFromLogId` marker (pmsSyncSlice): after `startSync` /
 * `saveAndSync` fires, every subscriber reads `running` until the server
 * returns a log whose id differs from the one that was current at request
 * time. The sync request itself is fire-and-forget — property-service runs the
 * paginated pull inline and can 504; the log row is what reports outcome.
 *
 * The identity check alone cannot terminate if the mutation resolves 2xx but
 * the backend never writes a new log row (a genuine backend bug, not a 504) —
 * every instance would poll "running" forever. `pendingPolls` (pmsSyncSlice)
 * bounds that: each NEW result that still shows the pre-sync log id
 * increments it (the mutation's own invalidation refetch is the first), and
 * MAX_PENDING_POLLS worth of them abandons the marker. The dedup (so N
 * simultaneously-subscribed instances observing the same result don't count
 * it N times) lives in the slice's `lastCountedPollAt`, keyed on the query's
 * `fulfilledTimeStamp` — not in a per-hook-instance ref.
 *
 * DV-3345: the account is explicit, and each account has its own cache entry.
 * `pmsSyncSlice`'s marker carries no account: every /home surface in a tab
 * names the same one, and the slice resets itself on every scope change, so a
 * baseline is never compared against another account's log.
 */
export function useSyncProgress({ origin, account, skip = false }: SyncProgressOptions): SyncProgress {
  // Typed dispatch so `getSyncLog.initiate(account)` below returns the query-thunk
  // promise (with `.unsubscribe()`), not the plain-action return of the
  // untyped `useDispatch`. Type-only import: no runtime cycle with the store.
  const dispatch = useDispatch<AppDispatch>();
  const { pendingFromLogId, pendingOrigin, startedLogId, startedOrigin, pendingPolls } =
    useSelector(selectPmsSync);
  const [syncProperties] = useSyncPropertiesMutation();
  const [saveConnectionAndSync] = useSaveConnectionAndSyncMutation();

  // Selector reference must be stable across renders, so it is memoized per
  // account: `select(arg)` builds a new memoized selector on every call.
  const selectCachedSyncLog = useMemo(
    () => pmsConnectionApi.endpoints.getSyncLog.select(account ?? skipToken),
    [account],
  );
  // Read the cached view BEFORE subscribing so the polling decision below
  // can use it in the same render (the query hook's own `data` is only
  // available after the call).
  const cachedEntry = useSelector(selectCachedSyncLog);
  const cached = cachedEntry.data ?? NEVER;
  const isPageVisible = usePageVisible();
  // Don't hit /syncLogs every 10s from a backgrounded tab.
  const shouldPoll =
    !skip &&
    account !== null &&
    isPageVisible &&
    (pendingFromLogId !== null || cached.kind === 'running');
  const { data, fulfilledTimeStamp } = useGetSyncLogQuery(skip || account === null ? skipToken : account, {
    pollingInterval: shouldPoll ? SYNC_POLL_INTERVAL_MS : 0,
  });
  // A firing-only instance (`skip: true`, used by the actions hook and the
  // settings save) has no `data` of its own but must still record the
  // CURRENT log id when it fires — so fall back to the shared cache entry.
  const server = data ?? cached;
  const serverId = idOf(server);

  // Identity rule: a different log id means the backend has picked up our
  // request. Any instance may observe it; the slice makes it visible to all.
  // While the id hasn't changed yet, report each poll result (the slice dedups
  // by `fulfilledTimeStamp`, so it's safe to dispatch on every result) and give
  // up once MAX_PENDING_POLLS distinct ones are reached (see the doc above).
  useEffect(() => {
    if (pendingFromLogId === null) return;
    if (serverId !== pendingFromLogId) {
      dispatch(syncObserved(serverId));
      return;
    }
    if (fulfilledTimeStamp !== undefined) {
      dispatch(syncPollObserved(fulfilledTimeStamp));
    }
    if (pendingPolls >= MAX_PENDING_POLLS) {
      dispatch(syncAbandoned());
    }
  }, [dispatch, pendingFromLogId, serverId, fulfilledTimeStamp, pendingPolls]);

  const optimistic = pendingFromLogId !== null && serverId === pendingFromLogId;
  const sync: SyncLogView = optimistic
    ? { kind: 'running', id: OPTIMISTIC_ID, startedAt: null }
    : server;

  /**
   * The baseline id a sync is measured against must be the REAL current log,
   * not the `never` placeholder shown while the first fetch is still in
   * flight: firing against 'none' while the server holds 'log-1' would make
   * that first fetch look like "a new log arrived" and read complete at once
   * (PR 1 review finding). Null while nothing has resolved yet.
   *
   * Seeded from the SHARED cache entry, not this instance's own query result:
   * the firing instance is usually a skip:true one (settings save, re-enable)
   * whose own fulfilledTimeStamp is undefined, and a null seed would let a
   * subscribed instance count the already-seen pre-sync fetch toward
   * MAX_PENDING_POLLS (see the slice's syncRequested doc).
   *
   * Only a SUCCESSFUL fetch qualifies. An errored one has no data, so the
   * only id on offer is the same 'none' sentinel — accepting it reintroduces
   * the false-complete above on the error path (PR 2 review blocker).
   */
  const resolvedBaseline: SyncBaseline | null =
    cachedEntry.isSuccess && cachedEntry.data !== undefined
      ? { fromLogId: serverId, fulfilledAt: cachedEntry.fulfilledTimeStamp ?? null }
      : null;
  // Read at fire time, not render time, so `fire` stays referentially stable.
  const resolvedBaselineRef = useRef(resolvedBaseline);
  resolvedBaselineRef.current = resolvedBaseline;

  /**
   * Nothing has resolved yet: join the in-flight fetch (RTK dedupes to the
   * same request). Null when that fetch fails — there is no honest baseline
   * to measure a sync against, so the caller must not fire.
   */
  const fetchBaseline = useCallback(async (): Promise<SyncBaseline | null> => {
    if (account === null) return null;
    const request = dispatch(pmsConnectionApi.endpoints.getSyncLog.initiate(account));
    try {
      const result = await request;
      if (result.data === undefined) return null;
      return { fromLogId: idOf(result.data), fulfilledAt: result.fulfilledTimeStamp ?? null };
    } finally {
      request.unsubscribe();
    }
  }, [account, dispatch]);

  // The deferred path has a window where nothing observable has changed yet
  // (`syncRequested` is only dispatched once the baseline resolves, so the
  // buttons stay live): a second click in that window must join the first
  // request, not send a second POST.
  const inFlightRef = useRef<Promise<SyncRequestOutcome> | null>(null);

  const fire = useCallback(
    (send: () => Promise<unknown>): Promise<SyncRequestOutcome> => {
      // `send` is a thunk, not an already-started promise: the mutation must
      // not fire before the baseline is known.
      const start = (baseline: SyncBaseline): Promise<SyncRequestOutcome> => {
        dispatch(syncRequested({ ...baseline, origin }));
        return send().then(
          (): SyncRequestOutcome => 'accepted',
          (error: unknown): SyncRequestOutcome => {
            const status = logSyncError(error);
            // A 504 means the sync is genuinely still running server-side
            // (property-service's paginated pull can outlive the gateway
            // timeout) — stay optimistic and let the poll-count cap above
            // bound the wait instead.
            if (status === 504) return 'accepted';
            // Fast failures (400/500 before any log row) would otherwise leave
            // every page polling forever, so abandon immediately.
            dispatch(syncAbandoned());
            return 'rejected';
          },
        );
      };
      const baseline = resolvedBaselineRef.current;
      // Fire synchronously in the common case (the log has already resolved);
      // only an unresolved first fetch defers the request.
      if (baseline !== null) return start(baseline);
      if (inFlightRef.current !== null) return inFlightRef.current;
      // No baseline means no way to tell the sync's own log from the one
      // already there — fail closed rather than fire and mis-announce.
      const deferred = fetchBaseline()
        .then((fetched): Promise<SyncRequestOutcome> | SyncRequestOutcome =>
          fetched === null ? 'rejected' : start(fetched),
        )
        .finally(() => {
          inFlightRef.current = null;
        });
      inFlightRef.current = deferred;
      return deferred;
    },
    [dispatch, fetchBaseline, origin],
  );

  const startSync = useCallback(
    (pmsSource: PmsSourceKey): Promise<SyncRequestOutcome> =>
      account === null
        ? Promise.resolve('rejected')
        : fire(() => syncProperties({ account, pmsSource }).unwrap()),
    [account, fire, syncProperties],
  );

  const saveAndSync = useCallback(
    (body: PMSVendorData): Promise<SyncRequestOutcome> =>
      account === null
        ? Promise.resolve('rejected')
        : fire(() => saveConnectionAndSync({ account, body }).unwrap()),
    [account, fire, saveConnectionAndSync],
  );

  const lastSyncedLabel =
    server.kind === 'complete' && server.endedAt
      ? `Last synced ${dayjs(server.endedAt).fromNow()}`
      : null;

  const completedInSession =
    server.kind === 'complete' && startedLogId !== null && server.id === startedLogId;
  const completedOrigin: SyncOrigin | null = completedInSession ? startedOrigin : null;
  // Same log-id guard as `completedInSession` above, and for the same reason:
  // `startedOrigin` outlives the sync it describes (it is only cleared on the
  // owner's unmount, or replaced by a later `syncObserved`), so falling back to
  // it unconditionally would attribute a sync started elsewhere — another tab,
  // a teammate — to this page, and the banner would claim "Property sync is
  // running." for work it never kicked off. `pendingOrigin` needs no guard: it
  // is set at request time and covers the optimistic window, where `sync.id` is
  // the placeholder OPTIMISTIC_ID rather than a real log id.
  const runningOrigin: SyncOrigin | null =
    sync.kind !== 'running'
      ? null
      : (pendingOrigin ?? (startedLogId !== null && sync.id === startedLogId ? startedOrigin : null));

  // The completion story is owned by the surface that started the sync: when
  // that surface unmounts, its "Property sync complete." must not be waiting
  // on the next visit. Two cases, because the story is attributed in two
  // steps — `pendingOrigin` at request time, `startedOrigin` once the new log
  // is observed: an owner leaving AFTER completion clears the story; one
  // leaving MID-sync detaches from it so `syncObserved` never attributes it
  // (PR 2 review finding). The in-flight request itself is deliberately left
  // alone — the sync is still running and every page should keep showing it.
  // Refs, because the cleanup closes over the values from the last render.
  const startedOriginRef = useRef(startedOrigin);
  startedOriginRef.current = startedOrigin;
  const pendingOriginRef = useRef(pendingOrigin);
  pendingOriginRef.current = pendingOrigin;
  useEffect(
    () => () => {
      if (skip) return;
      if (startedOriginRef.current === origin) dispatch(syncCompletionCleared());
      if (pendingOriginRef.current === origin) dispatch(syncOwnerDetached(origin));
    },
    [dispatch, origin, skip],
  );

  return {
    sync,
    startSync,
    saveAndSync,
    lastSyncedLabel,
    completedInSession,
    completedOrigin,
    runningOrigin,
  };
}
